import React, { useEffect, useRef, useState } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import EmojiPickerButton from './EmojiPickerButton';
import { templateApi } from '../../services/emailApi';
import '../TemplateManagement.css';

/* ── Allowed attachment max size ── */
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const BodySection = ({ formData, errors = {}, onChange, attachments, onAttachmentsChange, templateId }) => {
  const quillWrapperRef = useRef(null);
  const quillRef = useRef(null);
  const quillInstance = useRef(null);
  const quillChangeHandler = useRef(null);
  const greetingInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Ref always holds the latest attachments — immune to stale closures
  const attachmentsRef = useRef(attachments || []);
  // Local state drives the render
  const [localAttachments, setLocalAttachments] = useState(attachments || []);

  // Sync ref + local state when parent prop changes (e.g. template load)
  useEffect(() => {
    const incoming = attachments || [];
    attachmentsRef.current = incoming;
    setLocalAttachments(incoming);
  }, [attachments]);

  // Keep templateId in a ref so async callbacks always see the latest value
  const templateIdRef = useRef(templateId);
  useEffect(() => { templateIdRef.current = templateId; }, [templateId]);

  /** Helper: add an attachment to ref, local state AND parent state */
  const addAttachment = (item) => {
    const updated = [...attachmentsRef.current, item];
    attachmentsRef.current = updated;
    setLocalAttachments(updated);
    if (onAttachmentsChange) onAttachmentsChange(updated);
  };

  /** Helper: replace the full attachments list */
  const replaceAttachments = (list) => {
    const arr = Array.isArray(list) ? list : [];
    attachmentsRef.current = arr;
    setLocalAttachments(arr);
    if (onAttachmentsChange) onAttachmentsChange(arr);
  };

  useEffect(() => {
    const quillEl = quillRef.current;
    if (quillEl && !quillInstance.current) {
      quillInstance.current = new Quill(quillEl, {
        theme: 'snow',
        modules: {
          toolbar: {
            container: [
              [{ header: [1, 2, 3, false] }],
              ['bold', 'italic', 'underline', 'strike'],
              [{ color: [] }, { background: [] }],
              [{ list: 'ordered' }, { list: 'bullet' }],
              [{ align: [] }],
              ['link', 'image'],
              ['clean']
            ],
          }
        },
        placeholder: 'Compose your email content here...'
      });

      // Override Quill's built-in image handler to use our unified upload endpoint
      quillInstance.current.getModule('toolbar').addHandler('image', () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();
        input.onchange = async () => {
          const file = input.files?.[0];
          if (!file) return;
          try {
            setUploading(true);
            setUploadError('');
            const res = await templateApi.upload(file);
            const result = res?.data?.data || res?.data;
            const url = result?.url;
            if (!url) throw new Error('No URL returned from server');
            const quill = quillInstance.current;
            const range = quill.getSelection(true);
            quill.insertEmbed(range.index, 'image', url);
            quill.setSelection(range.index + 1);
          } catch (err) {
            console.error('[BodySection] Toolbar image upload failed:', err);
            setUploadError(err?.response?.data?.message || err?.message || 'Image upload failed.');
            setTimeout(() => setUploadError(''), 5000);
          } finally {
            setUploading(false);
          }
        };
      });

      quillInstance.current.root.innerHTML = formData.bodyContent || '';

      quillChangeHandler.current = () => {
        const content = quillInstance.current.root.innerHTML;
        onChange({ bodyContent: content });
      };
      quillInstance.current.on('text-change', quillChangeHandler.current);
    }

    return () => {
      if (quillInstance.current && quillChangeHandler.current) {
        quillInstance.current.off('text-change', quillChangeHandler.current);
        quillChangeHandler.current = null;
      }
      if (quillEl) {
        // Quill places toolbar as a sibling before the editor container
        const toolbarEl = quillEl.previousSibling;
        if (toolbarEl?.classList?.contains('ql-toolbar')) {
          toolbarEl.remove();
        }
        quillEl.innerHTML = '';
      }
      if (quillInstance.current) {
        quillInstance.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (quillInstance.current && formData.bodyContent !== quillInstance.current.root.innerHTML) {
      quillInstance.current.root.innerHTML = formData.bodyContent || '';
    }
  }, [formData.bodyContent]);

  const updateBody = (field, value) => {
    onChange({ [field]: value });
  };

  const insertVariable = (variableKey) => {
    if (quillInstance.current) {
      const range = quillInstance.current.getSelection();
      if (range) {
        quillInstance.current.insertText(range.index, `{{${variableKey}}}`);
      } else {
        const length = quillInstance.current.getLength();
        quillInstance.current.insertText(length - 1, `{{${variableKey}}}`);
      }
    }
  };

  const insertVariableInGreeting = (variableKey) => {
    const greetingInput = greetingInputRef.current;
    if (greetingInput) {
      const cursorPos = greetingInput.selectionStart || formData.bodyGreeting.length;
      const textBefore = formData.bodyGreeting.substring(0, cursorPos);
      const textAfter = formData.bodyGreeting.substring(cursorPos);
      updateBody('bodyGreeting', textBefore + `{{${variableKey}}}` + textAfter);
    } else {
      updateBody('bodyGreeting', formData.bodyGreeting + `{{${variableKey}}}`);
    }
  };

  const handleGreetingEmoji = (emoji) => {
    const input = greetingInputRef.current;
    const currentVal = formData.bodyGreeting || '';
    if (input) {
      const cursorPos = input.selectionStart ?? currentVal.length;
      const before = currentVal.substring(0, cursorPos);
      const after = currentVal.substring(cursorPos);
      updateBody('bodyGreeting', before + emoji + after);
      requestAnimationFrame(() => {
        input.focus();
        const newPos = cursorPos + emoji.length;
        input.setSelectionRange(newPos, newPos);
      });
    } else {
      updateBody('bodyGreeting', currentVal + emoji);
    }
  };

  const handleContentEmoji = (emoji) => {
    if (quillInstance.current) {
      const range = quillInstance.current.getSelection();
      if (range) {
        quillInstance.current.insertText(range.index, emoji);
      } else {
        const length = quillInstance.current.getLength();
        quillInstance.current.insertText(length - 1, emoji);
      }
      // Quill fires text-change automatically, which calls onChange
    }
  };

  /* ── File / PDF attachment upload ── */
  const handleAttachFile = () => {
    fileInputRef.current?.click();
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so the same file can be re-selected
    e.target.value = '';

    console.log('[BodySection] File selected:', file.name, 'type:', file.type, 'size:', file.size);

    // Validate size only — backend validates type
    if (file.size > MAX_FILE_SIZE) {
      setUploadError('File too large. Maximum size is 10 MB.');
      setTimeout(() => setUploadError(''), 4000);
      return;
    }

    try {
      setUploading(true);
      setUploadError('');

      const currentTemplateId = templateIdRef.current;

      if (currentTemplateId) {
        // ── Existing template: use the one-call upload+attach endpoint ──
        console.log('[BodySection] Uploading via POST /:templateId/attachments ...');
        const res = await templateApi.addAttachment(currentTemplateId, file);
        console.log('[BodySection] addAttachment response:', res?.data);
        const payload = res?.data?.data || res?.data;

        // Backend returns the full updated bodyAttachments array — use it
        if (payload?.bodyAttachments) {
          console.log('[BodySection] Replacing attachments with server list:', payload.bodyAttachments);
          replaceAttachments(payload.bodyAttachments);
        } else if (payload?.attachment) {
          // Fallback if backend doesn't return the full list
          const att = payload.attachment;
          addAttachment({ name: att.name, url: att.url, s3Key: att.s3Key, size: att.size, type: att.type });
        }
      } else {
        // ── New template (no ID yet): use generic upload, store s3Key ──
        console.log('[BodySection] Uploading via generic /upload endpoint (new template)...');
        const res = await templateApi.upload(file);
        console.log('[BodySection] Upload response:', res?.data);
        const result = res?.data?.data || res?.data;
        if (!result?.url) throw new Error('No URL returned from server');

        console.log('[BodySection] Adding to attachments');
        addAttachment({
          name: result.name || result.fileName || file.name,
          url: result.url,
          s3Key: result.s3Key || '',
          size: result.size || file.size,
          type: result.type || file.type,
        });
      }
    } catch (err) {
      console.error('[BodySection] File upload failed:', err);
      const msg = err?.response?.data?.message || err?.message || 'Upload failed. Please try again.';
      setUploadError(msg);
      setTimeout(() => setUploadError(''), 5000);
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = async (index) => {
    const att = attachmentsRef.current[index];
    const currentTemplateId = templateIdRef.current;

    // Optimistic UI removal
    const updated = attachmentsRef.current.filter((_, i) => i !== index);
    attachmentsRef.current = updated;
    setLocalAttachments(updated);
    if (onAttachmentsChange) onAttachmentsChange(updated);

    // If saved template, also tell the backend to remove
    if (currentTemplateId && att) {
      try {
        const identifier = att.s3Key ? { s3Key: att.s3Key } : { name: att.name };
        console.log('[BodySection] Removing attachment from backend:', identifier);
        const res = await templateApi.removeAttachment(currentTemplateId, identifier);
        const payload = res?.data?.data || res?.data;
        // Sync with server's authoritative list
        if (payload?.bodyAttachments) {
          replaceAttachments(payload.bodyAttachments);
        }
      } catch (err) {
        console.error('[BodySection] Backend remove failed (UI already updated):', err);
      }
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '0 KB';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="emtpl-section">
      <div className="emtpl-row">
        <div className="emtpl-group full-width">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label className="em-label required" style={{ marginBottom: 0 }}>Greeting</label>
            <EmojiPickerButton onSelect={handleGreetingEmoji} />
          </div>

          <div className="emtpl-var-toolbar">
            <span className="emtpl-var-toolbar-label">Insert:</span>
            {formData.variables?.map((variable) => (
              <button
                key={variable.key}
                type="button"
                className="emtpl-var-btn"
                onClick={() => insertVariableInGreeting(variable.key)}
                title={variable.description}
              >
                {variable.key}
              </button>
            ))}
          </div>

          <input
            ref={greetingInputRef}
            type="text"
            className={`em-input${errors.bodyGreeting ? ' em-input-error' : ''}`}
            data-greeting-input="true"
            value={formData.bodyGreeting}
            onChange={(e) => updateBody('bodyGreeting', e.target.value)}
            placeholder="Dear {{memberName}},"
          />
          {errors.bodyGreeting && <span className="em-error-text">{errors.bodyGreeting}</span>}
        </div>
      </div>

      <div className="emtpl-row">
        <div className="emtpl-group full-width">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label className="em-label required" style={{ marginBottom: 0 }}>Email Content</label>
            <EmojiPickerButton onSelect={handleContentEmoji} />
          </div>

          <div className="emtpl-var-toolbar">
            <span className="emtpl-var-toolbar-label">Insert:</span>
            {formData.variables?.map((variable) => (
              <button
                key={variable.key}
                type="button"
                className="emtpl-var-btn"
                onClick={() => insertVariable(variable.key)}
                title={variable.description}
              >
                {variable.key}
              </button>
            ))}
          </div>

          {errors.bodyContent && <span className="em-error-text" style={{ display: 'block', marginBottom: '4px' }}>{errors.bodyContent}</span>}

          {/* Quill wrapper — keeps toolbar + container together for CSS targeting */}
          <div ref={quillWrapperRef} className={`emtpl-editor-wrapper${errors.bodyContent ? ' em-input-error' : ''}`}>
            <div ref={quillRef} className="emtpl-editor" />
          </div>

          {/* ── Attach File button ── */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '8px',
            flexWrap: 'wrap',
          }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
              style={{ display: 'none' }}
              onChange={onFileSelected}
            />
            <button
              type="button"
              onClick={handleAttachFile}
              disabled={uploading}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '7px 16px',
                background: '#f8fafc',
                color: '#334155',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: uploading ? 'not-allowed' : 'pointer',
                opacity: uploading ? 0.6 : 1,
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { if (!uploading) { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.borderColor = '#94a3b8'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
              title="Attach a file — images go into the editor, documents go to attachments (max 10 MB)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
              Attach File
            </button>

            {uploading && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#4338ca' }}>
                <div className="emtpl-spinner" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />
                Uploading...
              </span>
            )}
          </div>

          {uploadError && (
            <div style={{ padding: '8px 12px', marginTop: '6px', background: '#fef2f2', borderRadius: '6px', fontSize: '13px', color: '#dc2626' }}>
              {uploadError}
            </div>
          )}
        </div>
      </div>

      {/* ── Attachment List ── */}
      <div className="emtpl-row">
        <div className="emtpl-group full-width">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <label className="em-label" style={{ marginBottom: 0 }}>
              📎 Attachments {localAttachments.length > 0 && <span style={{ color: '#64748b', fontWeight: 400 }}>({localAttachments.length})</span>}
            </label>
          </div>

          <p style={{ fontSize: '12px', color: '#94a3b8', margin: '0 0 8px' }}>
            Non-image files (PDF, DOC, XLS, TXT) appear here. Images are inserted into the editor body.
          </p>

          {localAttachments.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {localAttachments.map((att, idx) => (
                <div
                  key={att.url || idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 12px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '13px',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#334155' }}>
                    {att.name}
                  </span>
                  <span style={{ color: '#94a3b8', fontSize: '12px', flexShrink: 0 }}>
                    {formatSize(att.size)}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(idx)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#ef4444',
                      padding: '2px',
                      display: 'flex',
                      alignItems: 'center',
                      flexShrink: 0,
                    }}
                    title="Remove attachment"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              padding: '16px',
              border: '1px dashed #cbd5e1',
              borderRadius: '8px',
              textAlign: 'center',
              color: '#94a3b8',
              fontSize: '13px',
            }}>
              No attachments added yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BodySection;
